// LeadCarrierMatchPanel — Phase 6.
//
// Fetches /api/leads/carrier-match. Renders the top plan matches
// with fit score, approval odds, monthly premium, deductible, and
// estimated commission. Hides itself if the operator hasn't
// imported plans yet (empty library) — the empty-state alternative
// is a one-line hint about how to load plans.

import React from 'react';
import { readPersistedJwtDirect } from './authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmt$(cents) {
  if (!Number.isFinite(cents) || cents === 0) return '$—';
  return `$${(cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

function FitBar({ score }) {
  const color = score >= 75 ? '#059669' : score >= 55 ? '#d97706' : '#94a3b8';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 4, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden', minWidth: 40 }}>
        <div style={{ width: `${score}%`, height: '100%', background: color }}/>
      </div>
      <div style={{ fontSize: 10, fontWeight: 800, color, fontFamily: 'JetBrains Mono, monospace' }}>{score}</div>
    </div>
  );
}

export default function LeadCarrierMatchPanel({ lead }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });

  React.useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const { authedFetchLead } = await import('./authedFetch');
      const res = await authedFetchLead('/api/leads/carrier-match', { lead }, controller.signal);
      if (controller.signal.aborted) return;
      setState({ loading: false, data: res.ok ? res.data : null, error: res.ok ? null : res.error });
    })();
    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  if (state.loading) return null;
  if (state.error) {
    return (
      <div style={{
        background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10,
        padding: 10, fontSize: 11, color: '#92400e', marginBottom: 8,
      }}>
        Carrier Match Engine temporarily unavailable ({String(state.error)}).
      </div>
    );
  }
  const d = state.data;
  if (!d) return null;

  // Empty plan library — hint but don't clutter with a big empty card.
  if (d.plan_library_empty) return null;

  const matches = d.matches || [];
  if (matches.length === 0) return null;

  return (
    <div style={{
      background: '#f0fdfa',
      border: '1px solid #99f6e4',
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#0f766e', fontWeight: 800 }}>
          Carrier Match Engine
        </div>
        <div style={{ fontSize: 11, color: '#134e4a' }}>
          {matches.length} of {d.total_plans_scanned} plans ranked{d.lead_state && ` · ${d.lead_state}`}
        </div>
      </div>

      <div style={{
        background: '#ffffff', border: '1px solid #ccfbf1', borderRadius: 8,
        overflow: 'hidden', overflowX: 'auto',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ background: '#f0fdfa' }}>
              {['Carrier / plan', 'Metal / net', 'Premium', 'Deductible', 'Fit', 'Approval', 'Commission'].map(h => (
                <th key={h} style={{
                  textAlign: 'left', padding: '6px 10px', color: '#0f766e',
                  fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 800,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matches.map(m => (
              <tr key={m.plan_id} style={{ borderTop: '1px solid #ccfbf1' }}>
                <td style={{ padding: '6px 10px' }}>
                  <div style={{ color: '#0f172a', fontWeight: 700 }}>{m.carrier}</div>
                  <div style={{ color: '#64748b', fontSize: 10 }}>{m.plan_name}</div>
                </td>
                <td style={{ padding: '6px 10px', color: '#334155' }}>
                  {m.metal || '—'} / {m.network_type || '—'}
                </td>
                <td style={{ padding: '6px 10px', color: m.monthly_premium_cents == null ? '#94a3b8' : '#334155', fontFamily: 'JetBrains Mono, monospace', fontVariantNumeric: 'tabular-nums', fontStyle: m.monthly_premium_cents == null ? 'italic' : 'normal' }}>
                  {m.monthly_premium_cents == null
                    ? <span title="ACA premiums vary by income + subsidy — no static value on the plan library">varies</span>
                    : `${fmt$(m.monthly_premium_cents)}/mo`}
                </td>
                <td style={{ padding: '6px 10px', color: '#334155', fontFamily: 'JetBrains Mono, monospace', fontVariantNumeric: 'tabular-nums' }}>
                  {fmt$(m.deductible_cents)}
                </td>
                <td style={{ padding: '6px 10px', minWidth: 90 }}>
                  <FitBar score={m.fit_score}/>
                </td>
                <td style={{ padding: '6px 10px', color: m.approval_odds_pct >= 80 ? '#059669' : '#d97706', fontWeight: 800 }}>
                  {m.approval_odds_pct}%
                </td>
                <td style={{ padding: '6px 10px', color: '#059669', fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', fontVariantNumeric: 'tabular-nums' }}>
                  ~{fmt$(m.estimated_commission_cents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
